import type { ContentNode, DocumentState } from '../document-types';
import type { PositionedNodeHandler, RenderContext } from './types';
import { normalizeJsonNode, type JSONNode } from '../json-node';

// Pretty printer that works directly with JSONNode objects
const prettyPrintNode = (node: JSONNode, indentLevel: number = 0): string => {
  const indent = '  '.repeat(indentLevel);
  const nextIndent = '  '.repeat(indentLevel + 1);

  switch (node.t) {
    case 'object': {
      const fieldEntries = Object.entries(node.fields).sort(([, a], [, b]) =>
        (a.name ?? '').localeCompare(b.name ?? '')
      );
      if (fieldEntries.length === 0) {
        return `${indent}{\n${indent}}`;
      }

      const formattedFields = fieldEntries.map(([_key, field]) => {
        const fieldType = prettyPrintNode(field, indentLevel + 1);
        const nullableSuffix = field.nullable ? ' | null' : '';
        // Remove all leading indentation that was added by the recursive call
        const cleanFieldType = fieldType.replace(/^\s+/, '');
        // Use field.name exactly as-is
        const fieldName = field.name ?? '';
        return `${nextIndent}"${fieldName}": ${cleanFieldType}${nullableSuffix}`;
      });

      return `${indent}{\n${formattedFields.join(',\n')}\n${indent}}`;
    }

    case 'array': {
      const itemType = prettyPrintNode(node.items.item, indentLevel + 1);
      // Add proper indentation to the item
      const indentedItem = itemType
        .split('\n')
        .map((line) => {
          return line.startsWith('  ') ? line : `${nextIndent}${line}`;
        })
        .join('\n');
      return `${indent}[\n${indentedItem}\n${indent}]`;
    }

    case 'enum': {
      return `"${node.values.join(' | ')}"`;
    }

    case 'string':
    case 'int':
    case 'float':
    case 'boolean':
    case 'timestamp': {
      return `"${node.t}"`;
    }
    default: {
      return `"unknown"`;
    }
  }
};

export class ResponseShapeHandler implements PositionedNodeHandler {
  readonly type = 'response-shape';
  readonly position = 'bottom' as const;

  render(
    node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    _childContent: string,
    _ctx: RenderContext
  ): string {
    const attrs = (node as ContentNode).attrs;
    const jsonNode = attrs?.['jsonNode'];

    if (jsonNode === undefined || jsonNode === null) {
      return '';
    }

    try {
      // Shapes are stored as opaque node attrs and are often LLM authored, so
      // a malformed one reaches us intact. Printing it unrepaired puts junk in
      // the prompt (`{ "": "unknown" }`) or throws into the catch below, which
      // drops the agent's output contract without a word either way.
      const prettyJson = prettyPrintNode(normalizeJsonNode(jsonNode));
      const res = `**Return the following JSON structure:**\n<json>${prettyJson}</json>\n\n`;
      // In expression mode, return plain string - parent toPromptMarkdown will handle wrapping
      return res;
    } catch {
      return '';
    }
  }
}
